import { Result } from './Result';
import { AuthService } from './AuthService';

export class HttpHelpers {
  // Helper method to log API calls
  logApiCall(method: string, url: string, data?: any): void {
    const timestamp = new Date().toISOString();
    console.log(`[${timestamp}] RestClient: ${method} ${url}`, data);
  }

  // Helper method to get headers with authentication
  getHeaders(additionalHeaders: Record<string, string> = {}): Record<string, string> {
    return {
      ...AuthService.getAuthHeader(),
      ...additionalHeaders
    };
  }

  // Helper method to create appropriate log data based on request content
  getLogData(request: any): any {
    if (!request) return request;
    
    // For auth requests, hide password
    if (request.username && request.password) {
      return { username: request.username };
    }
    
    // For player arrays, show count instead of full data
    if (request.players && Array.isArray(request.players)) {
      return { 
        ...request, 
        players: `${request.players.length} players` 
      };
    }
    
    // For team capture requests, show summary
    if (request.team1 && request.team2) {
      return {
        team1Size: request.team1.length,
        team2Size: request.team2.length,
        team1Info: request.team1Info?.name || "Team 1",
        team2Info: request.team2Info?.name || "Team 2"
      };
    }
    
    // For text messages, show length instead of full content
    if (request.message && typeof request.message === 'string') {
      return {
        ...request,
        message: `${request.message.length} characters`
      };
    }
    
    // For single players, show name/id
    if (request.name && request.id !== undefined) {
      return { id: request.id, name: request.name };
    }
    
    return request;
  }

  // Generic fetch method for requests with body (POST, PUT, etc.)
  async fetchWithBody<TRequest, TResponse>(
    url: string, 
    method: string, 
    request: TRequest, 
    operation: string
  ): Promise<Result<TResponse>> {
    // Enhanced logging for different request types
    const logData = this.getLogData(request);
    this.logApiCall(method, url, logData);
    
    try {
      const response = await fetch(url, {
        method: method,
        headers: this.getHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(request)
      });
      
      if (!response.ok) {
        return await this.handleApiError<TResponse>(response, operation);
      }

      // Treat 204/205 as empty responses
      if (response.status === 204 || response.status === 205) {
        return Result.Success(undefined as unknown as TResponse);
      }

      const contentType = response.headers.get('content-type') || '';

      // If the response is not JSON, treat it as an empty/successful response
      if (!contentType.toLowerCase().includes('application/json')) {
        return Result.Success(undefined as unknown as TResponse);
      }

      // Safely handle potentially empty JSON bodies
      // Note: We use text() instead of relying on content-length header
      // because content-length may not be accessible due to CORS restrictions
      const responseText = await response.text();
      if (!responseText) {
        return Result.Success(undefined as unknown as TResponse);
      }

      const responseData: TResponse = JSON.parse(responseText);
      return Result.Success(responseData);
    } catch (error) {
      console.error(`[RestClient] Network/parsing error in ${operation.toLowerCase()}:`, error);
      return Result.Failure<TResponse>('NETWORK_ERROR', error instanceof Error ? error.message : 'Network error occurred');
    }
  }

  // Generic fetch method for requests without body (GET, DELETE, etc.)
  async fetchWithoutBody<TResponse>(
    url: string, 
    method: string, 
    operation: string, 
    requiresAuth: boolean = true
  ): Promise<Result<TResponse>> {
    this.logApiCall(method, url);
    
    try {
      const headers = requiresAuth ? this.getHeaders() : {};
      const response = await fetch(url, {
        method: method,
        headers: headers
      });
      
      if (!response.ok) {
        return await this.handleApiError<TResponse>(response, operation);
      }
      
      const responseData: TResponse = await response.json();
      return Result.Success(responseData);
    } catch (error) {
      console.error(`[RestClient] Network/parsing error in ${operation.toLowerCase()}:`, error);
      return Result.Failure<TResponse>('NETWORK_ERROR', error instanceof Error ? error.message : 'Network error occurred');
    }
  }

  // Special method for blob responses (like file downloads)
  async fetchBlob<TRequest>(
    url: string,
    method: string,
    request: TRequest,
    operation: string
  ): Promise<Result<Blob>> {
    const logData = this.getLogData(request);
    this.logApiCall(method, url, logData);

    try {
      const response = await fetch(url, {
        method: method,
        headers: this.getHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        return await this.handleApiError<Blob>(response, operation);
      }

      const blob: Blob = await response.blob();
      return Result.Success(blob);
    } catch (error) {
      console.error(`[RestClient] Network/parsing error in ${operation.toLowerCase()}:`, error);
      return Result.Failure<Blob>('NETWORK_ERROR', error instanceof Error ? error.message : 'Network error occurred');
    }
  }

  // GET request returning blob (for file downloads without request body)
  async fetchBlobGet(
    url: string,
    operation: string
  ): Promise<Result<Blob>> {
    this.logApiCall('GET', url);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        return await this.handleApiError<Blob>(response, operation);
      }

      const blob: Blob = await response.blob();
      return Result.Success(blob);
    } catch (error) {
      console.error(`[RestClient] Network/parsing error in ${operation.toLowerCase()}:`, error);
      return Result.Failure<Blob>('NETWORK_ERROR', error instanceof Error ? error.message : 'Network error occurred');
    }
  }

  // POST with FormData (for file uploads)
  async fetchFormData<TResponse>(
    url: string,
    formData: FormData,
    operation: string
  ): Promise<Result<TResponse>> {
    this.logApiCall('POST', url, 'FormData upload');

    try {
      // Get auth headers but explicitly exclude Content-Type
      // Browser must set Content-Type with boundary for multipart/form-data
      const headers = this.getHeaders();
      delete (headers as Record<string, string>)['Content-Type'];

      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: formData
      });

      if (!response.ok) {
        return await this.handleApiError<TResponse>(response, operation);
      }

      const responseData: TResponse = await response.json();
      return Result.Success(responseData);
    } catch (error) {
      console.error(`[RestClient] Network/parsing error in ${operation.toLowerCase()}:`, error);
      return Result.Failure<TResponse>('NETWORK_ERROR', error instanceof Error ? error.message : 'Network error occurred');
    }
  }

  // DELETE request that expects no response body (204 No Content)
  async fetchDelete(
    url: string,
    operation: string
  ): Promise<Result<void>> {
    this.logApiCall('DELETE', url);

    try {
      const response = await fetch(url, {
        method: 'DELETE',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        return await this.handleApiError<void>(response, operation);
      }

      return Result.Success(undefined as unknown as void);
    } catch (error) {
      console.error(`[RestClient] Network/parsing error in ${operation.toLowerCase()}:`, error);
      return Result.Failure<void>('NETWORK_ERROR', error instanceof Error ? error.message : 'Network error occurred');
    }
  }

  // Handle API errors and convert to Result format
  async handleApiError<T>(response: Response, operation: string): Promise<Result<T>> {
    const timestamp = new Date().toISOString();
    
    // Handle 401 Unauthorized (token expired or invalid)
    if (response.status === 401) {
      console.error(`[${timestamp}] RestClient Error: Authentication failed for ${operation}`);
      AuthService.logout(); // Clear invalid token
      window.dispatchEvent(new CustomEvent('auth:session-expired'));
      return Result.Failure<T>('UNAUTHORIZED', 'Your session has expired. Please log in again.');
    }
    
    try {
      // Try to get ProblemDetails from response body
      const errorText = await response.text();
      if (errorText) {
        try {
          const problemDetails = JSON.parse(errorText);

          // RFC 7807 ProblemDetails format - use 'type' as error code
          if (problemDetails.type) {
            // Get error description from detail or format validation errors
            let description = problemDetails.detail;

            // Handle ValidationProblemDetails (has errors object instead of detail)
            if (!description && problemDetails.errors) {
              const errorMessages = Object.entries(problemDetails.errors)
                .flatMap(([field, messages]) =>
                  (messages as string[]).map(msg => field ? `${field}: ${msg}` : msg)
                );
              description = errorMessages.join('; ');
            }

            description = description || problemDetails.title || 'An error occurred';
            console.error(`[${timestamp}] RestClient Error: ${operation} failed with ${problemDetails.type}: ${description}`);
            return Result.Failure<T>(problemDetails.type, description);
          }

          // Legacy format - title as error code (backwards compatibility)
          if (problemDetails.title && problemDetails.detail) {
            console.error(`[${timestamp}] RestClient Error: ${operation} failed with ${problemDetails.title}: ${problemDetails.detail}`);
            return Result.Failure<T>(problemDetails.title, problemDetails.detail);
          }

          // Legacy error format
          if (problemDetails.message || problemDetails.error) {
            const message = problemDetails.message || problemDetails.error;
            console.error(`[${timestamp}] RestClient Error: ${operation} failed: ${message}`);
            return Result.Failure<T>('API_ERROR', message);
          }
        } catch {
          // Not JSON, use text directly if reasonable length
          if (errorText.length < 200) {
            console.error(`[${timestamp}] RestClient Error: ${operation} failed: ${errorText}`);
            return Result.Failure<T>('API_ERROR', errorText);
          }
        }
      }
    } catch {
      // Fallback if we can't read response body
    }
    
    // Generic fallback error
    const errorMessage = `${operation} failed: ${response.status} ${response.statusText}`;
    console.error(`[${timestamp}] RestClient Error: ${errorMessage}`);
    console.error(`[${timestamp}] Response details:`, {
      status: response.status,
      statusText: response.statusText,
      url: response.url,
      headers: Object.fromEntries(response.headers.entries())
    });
    
    return Result.Failure<T>('HTTP_ERROR', errorMessage);
  }
}
